import { Button, Checkbox, Input, Select } from '@artosera/ui';
import type { CleModele } from '@artosera/shared';
import type { CSSProperties, JSX } from 'react';
import { useState } from 'react';
import { capturerPortee, estActuelle } from '../../auth/identite.js';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../api/errors.js';
import { useModeleDocument } from '../../api/modelesDocuments.js';
import {
  useModifierModeleVentes,
  type CorpsMiseAJourModele,
  type ModeleDocument,
  type PoliceModele,
} from '../../api/ventes.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { Field } from '../Field.js';
import { useShellContext } from '../shell/context.js';
import styles from './DocumentsPage.module.css';

/** Les documents de l'œuvre : fiche, certificat, liste de prix. */
const MODELES_OEUVRE: readonly CleModele[] = [
  'fiche_oeuvre',
  'certificat',
  'liste_prix',
  'dossier_oeuvres',
  'dossier_artiste',
];
/** Les pièces de vente : seulement avec le module `facturation` (D21). */
const MODELES_VENTES: readonly CleModele[] = ['facture', 'avoir'];
const POLICES: readonly PoliceModele[] = ['serif', 'sans', 'mono'];
/** Une couleur hexadécimale complète, la seule que l'aperçu reprend. */
const COULEUR_VALIDE = /^#[0-9a-fA-F]{6}$/;

/**
 * Réglages › Documents : les modèles des documents de la galerie — en-tête,
 * pied, couleurs, police. Les modèles personnalisés sont le module
 * `documents-prestige` (D21, page commerciale) : sans lui, l'écran n'est pas
 * proposé (la coquille montre « Module non activé ») et la fiche comme la
 * liste de prix du cœur sortent avec le modèle par défaut. Même API qu'avant
 * (`…/ventes/modeles/:cle`, `docs/dev/api.md` § 17.3).
 */
export function DocumentsPage(): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie, compte } = useShellContext();
  const peutRegler = galerieALaction(galerie, 'settings:templates');
  const cles: readonly CleModele[] = [
    ...MODELES_OEUVRE,
    ...(galerieAModule(galerie, 'offres-relances') ? ['offre' as const] : []),
    ...(galerieAModule(galerie, 'facturation') ? MODELES_VENTES : []),
    ...(galerieAModule(galerie, 'mouvements') ? (['bon_depot', 'bon_pret'] as const) : []),
  ];
  const [cleChoisie, setCle] = useState<CleModele>(cles[0] ?? 'fiche_oeuvre');
  const cle = cles.includes(cleChoisie) ? cleChoisie : (cles[0] ?? 'fiche_oeuvre');
  const modele = useModeleDocument(galerieSlug, cle);

  return (
    <div className={styles.page}>
      <h1 className={styles.titre}>{t('regie:reglages.documents.titre')}</h1>
      <p className={styles.sousTitre}>{t('regie:reglages.documents.sousTitre')}</p>

      {!peutRegler ? (
        <p className={styles.vide}>{t('regie:reglages.documents.refus')}</p>
      ) : (
        <section className={styles.atelier}>
          <div className={styles.choix}>
            <Field label={t('regie:reglages.documents.choix')} htmlFor="document-modele">
              <Select
                id="document-modele"
                value={cle}
                onValueChange={(valeur) => setCle(valeur as CleModele)}
                options={cles.map((valeur) => ({
                  value: valeur,
                  label: t(`regie:reglages.documents.modeles.${valeur}`),
                }))}
              />
            </Field>
          </div>

          {modele.isLoading ? (
            <p className={`${styles.formulaire} ${styles.vide}`}>{t('commun:chargement')}</p>
          ) : modele.isError || !modele.data ? (
            <p className={`${styles.formulaire} ${styles.erreur}`} role="alert">
              {estApiError(modele.error) ? modele.error.message : t('commun:erreurReseau')}
            </p>
          ) : (
            <FormulaireModele
              key={`${compte.id}-${galerieSlug}-${modele.data.modele.cle}-${modele.data.modele.modifieLe}`}
              galerieSlug={galerieSlug}
              modele={modele.data.modele}
            />
          )}
        </section>
      )}
    </div>
  );
}

function FormulaireModele({
  galerieSlug,
  modele,
}: {
  galerieSlug: string;
  modele: ModeleDocument;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const modifier = useModifierModeleVentes(galerieSlug);

  const [nom, setNom] = useState(modele.nom);
  const [entete, setEntete] = useState(modele.entete?.fr ?? '');
  const [pied, setPied] = useState(modele.pied?.fr ?? '');
  const [accent, setAccent] = useState(modele.couleurs.accent);
  const [encre, setEncre] = useState(modele.couleurs.encre);
  const [policeTitre, setPoliceTitre] = useState<PoliceModele>(modele.policeTitre ?? 'serif');
  const estCertificat = modele.cle === 'certificat';
  const [signataireNom, setSignataireNom] = useState(modele.signataire?.nom ?? '');
  const [signataireQualite, setSignataireQualite] = useState(modele.signataire?.qualite ?? '');
  const [lieuEmission, setLieuEmission] = useState(modele.lieuEmission ?? '');
  // Les étiquettes et cartels suivent le modèle de la fiche d'œuvre (ARTO-201).
  const gereLesEtiquettes = modele.cle === 'fiche_oeuvre';
  const [logoEtiquettes, setLogoEtiquettes] = useState(modele.logoEtiquettes);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState(false);

  async function soumettre(): Promise<void> {
    const portee = capturerPortee();
    setErreur(null);
    setEnregistre(false);
    const corps: CorpsMiseAJourModele = {
      nom,
      ...(entete.trim() ? { entete: { ...modele.entete, fr: entete.trim() } } : {}),
      ...(pied.trim() ? { pied: { ...modele.pied, fr: pied.trim() } } : {}),
      couleurs: { accent, encre },
      policeTitre,
      ...(gereLesEtiquettes ? { logoEtiquettes } : {}),
      ...(estCertificat
        ? {
            signataire: signataireNom.trim()
              ? {
                  nom: signataireNom.trim(),
                  ...(signataireQualite.trim() ? { qualite: signataireQualite.trim() } : {}),
                }
              : null,
            lieuEmission: lieuEmission.trim() || null,
          }
        : {}),
    };
    try {
      await modifier.mutateAsync({ cle: modele.cle, corps });
      if (estActuelle(portee)) setEnregistre(true);
    } catch (error) {
      if (estActuelle(portee))
        setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  // L'aperçu reprend les couleurs saisies, seulement quand elles sont
  // complètes (une saisie en cours ne fait pas clignoter la feuille).
  const teintes = {
    ...(COULEUR_VALIDE.test(accent) ? { '--doc-accent': accent } : {}),
    ...(COULEUR_VALIDE.test(encre) ? { '--doc-encre': encre } : {}),
  } as CSSProperties;
  const titreDocument = t(`regie:reglages.documents.modeles.${modele.cle}`);

  return (
    <>
      <div className={styles.formulaire}>
        <p className={styles.aide}>{t('regie:reglages.documents.aide')}</p>

        <Field label={t('regie:ventes.reglages.modeleNom')} htmlFor="modele-nom">
          <Input id="modele-nom" value={nom} onChange={(event) => setNom(event.target.value)} />
        </Field>

        <div className={styles.grille2}>
          <Field label={t('regie:ventes.reglages.entete')} htmlFor="modele-entete">
            <Input
              id="modele-entete"
              value={entete}
              onChange={(event) => setEntete(event.target.value)}
            />
          </Field>
          <Field label={t('regie:ventes.reglages.pied')} htmlFor="modele-pied">
            <Input
              id="modele-pied"
              value={pied}
              onChange={(event) => setPied(event.target.value)}
            />
          </Field>
        </div>

        <div className={styles.grille2}>
          <Field label={t('regie:ventes.reglages.couleurAccent')} htmlFor="modele-accent">
            <div className={styles.couleurChamp}>
              <input
                id="modele-accent"
                type="color"
                value={accent}
                onChange={(event) => setAccent(event.target.value)}
              />
              <span className={styles.couleurValeur}>{accent}</span>
            </div>
          </Field>
          <Field label={t('regie:ventes.reglages.couleurEncre')} htmlFor="modele-encre">
            <div className={styles.couleurChamp}>
              <input
                id="modele-encre"
                type="color"
                value={encre}
                onChange={(event) => setEncre(event.target.value)}
              />
              <span className={styles.couleurValeur}>{encre}</span>
            </div>
          </Field>
        </div>

        <Field label={t('regie:ventes.reglages.policeTitre')} htmlFor="modele-police">
          <Select
            id="modele-police"
            value={policeTitre}
            onValueChange={(valeur) => setPoliceTitre(valeur as PoliceModele)}
            options={POLICES.map((code) => ({
              value: code,
              label: t(`regie:ventes.reglages.polices.${code}`),
            }))}
          />
        </Field>

        {gereLesEtiquettes ? (
          <>
            <Checkbox
              id="modele-logo-etiquettes"
              label={t('regie:reglages.documents.logoEtiquettes')}
              checked={logoEtiquettes}
              onCheckedChange={(valeur) => setLogoEtiquettes(valeur === true)}
            />
            <p className={styles.aide}>{t('regie:reglages.documents.logoEtiquettesAide')}</p>
          </>
        ) : null}

        {estCertificat ? (
          <fieldset className={styles.signature}>
            <legend className={styles.signatureTitre}>
              {t('regie:reglages.documents.signature.titre')}
            </legend>
            <p className={styles.aide}>{t('regie:reglages.documents.signature.aide')}</p>
            <div className={styles.grille2}>
              <Field
                label={t('regie:reglages.documents.signature.nom')}
                htmlFor="modele-signataire-nom"
              >
                <Input
                  id="modele-signataire-nom"
                  value={signataireNom}
                  onChange={(event) => setSignataireNom(event.target.value)}
                />
              </Field>
              <Field
                label={t('regie:reglages.documents.signature.qualite')}
                htmlFor="modele-signataire-qualite"
              >
                <Input
                  id="modele-signataire-qualite"
                  value={signataireQualite}
                  onChange={(event) => setSignataireQualite(event.target.value)}
                />
              </Field>
            </div>
            <Field
              label={t('regie:reglages.documents.signature.lieu')}
              htmlFor="modele-lieu-emission"
            >
              <Input
                id="modele-lieu-emission"
                value={lieuEmission}
                onChange={(event) => setLieuEmission(event.target.value)}
              />
            </Field>
          </fieldset>
        ) : null}

        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        {enregistre && !erreur ? (
          <p className={styles.confirmation}>{t('regie:ventes.reglages.enregistre')}</p>
        ) : null}

        <div className={styles.actions}>
          <Button onClick={() => void soumettre()} loading={modifier.isPending}>
            {t('commun:actions.enregistrer')}
          </Button>
        </div>
      </div>

      <ApercuDocument
        cle={modele.cle}
        titre={titreDocument}
        entete={entete}
        pied={pied}
        police={policeTitre}
        teintes={teintes}
      />
    </>
  );
}

/**
 * La feuille du document, réduite : en-tête et filet à la couleur d'accent,
 * titre dans la police choisie et à la couleur d'encre, corps esquissé selon
 * le modèle (œuvre et cartel, liste de prix, lignes de facture), pied de
 * page. Décorative : tout ce qu'elle montre est déjà dans le formulaire.
 */
function ApercuDocument({
  cle,
  titre,
  entete,
  pied,
  police,
  teintes,
}: {
  cle: CleModele;
  titre: string;
  entete: string;
  pied: string;
  police: PoliceModele;
  teintes: CSSProperties;
}): JSX.Element {
  const corps =
    cle === 'facture' || cle === 'avoir' ? 'lignes' : cle === 'liste_prix' ? 'prix' : 'oeuvre';
  return (
    <aside className={styles.apercu} aria-hidden="true">
      <div className={styles.feuille} style={teintes} data-police={police} data-corps={corps}>
        <div className={styles.feuilleTete}>
          <span className={styles.feuilleEntete}>{entete}</span>
          <span className={styles.feuilleCoordonnees}>
            <span />
            <span />
          </span>
        </div>
        <span className={styles.feuilleFilet} />
        <span className={styles.feuilleTitre}>{titre}</span>

        {corps === 'oeuvre' ? (
          <div className={styles.feuilleOeuvre}>
            <span className={styles.feuilleImage} />
            <span className={styles.feuilleCartel}>
              <span />
              <span />
              <span />
              <span />
            </span>
          </div>
        ) : (
          <div className={styles.feuilleLignes}>
            {[0, 1, 2, 3, 4].map((rang) => (
              <span key={rang} className={styles.feuilleLigne}>
                <span />
                <span />
              </span>
            ))}
            {corps === 'lignes' ? <span className={styles.feuilleTotal} /> : null}
          </div>
        )}

        <span className={styles.feuillePied}>{pied}</span>
      </div>
    </aside>
  );
}
